import React from 'react';
import setNewFocused from './setNewFocused';
import { THandleKey, INewFocus, ISelectableState } from './types';
import { Draft } from '@reduxjs/toolkit';

/**
 * Este método es de uso interno y normalmente
 * no será necesario fuera de este documento,
 * su utilidad es la de determinar si un evento
 * de teclado será controlado por el keyHandler.
 */
export function willHandleKey(
  ev: React.KeyboardEvent,
  state: ISelectableState,
) {
  const actualKey = ev.key.toLowerCase();
  if (state.isEditionMode) return false;

  if (actualKey === ' ' && ev.ctrlKey) return true;
  if (actualKey === 'a' && ev.ctrlKey) return true;
  if (actualKey === 'm' && ev.ctrlKey) return true;
  if (state.draggingRow !== undefined) {
    return ['Escape', 'Enter'].includes(ev.key);
  }
  return [
    'arrowup',
    'arrowdown',
    'arrowleft',
    'arrowright',
    'end',
    'enter',
    'home',
    'pageup',
    'pagedown',
  ].includes(ev.key.toLowerCase());
}

/**
 * Este método será llamado normalmente desde un action
 * de Redux. Su utilidad es la de evaluar qué tecla
 * se presionó y decidir qué alteración se debe producir
 * en base a eso.
 */
export default function handleKey(
  state: Draft<ISelectableState>,
  { ev }: THandleKey,
) {
  const {
    altKey: isAltPressed,
    ctrlKey: isCtrlPressed,
    key,
    shiftKey: isShiftPressed,
  } = ev;
  if (willHandleKey(ev, state)) ev.preventDefault();
  const actualKey = key.toLowerCase();
  const setFocus = ({
    newFocusedColumn,
    newFocusedRow,
    ...rest
  }: Partial<INewFocus>) =>
    setNewFocused(state, {
      altKey: isAltPressed,
      ctrlKey: isCtrlPressed,
      shiftKey: isShiftPressed,
      newFocusedColumn,
      newFocusedRow,
      ...rest,
    });

  if (!state.isEditionMode) {
    if (
      actualKey === 'enter' &&
      (state.allowEdition === true || state.focusedRow < 0)
    )
      state.isEditionMode = true;
    else
      switch (actualKey) {
        case 'arrowup': {
          let sumRate = state.expandedRows.includes(state.focusedRow - 1)
            ? 0.6
            : 1;
          if (Math.round(state.focusedRow) - state.focusedRow !== 0)
            sumRate = 0.5;
          setFocus({
            newFocusedRow: state.focusedRow - sumRate,
            shouldSelectNewFocused: !isCtrlPressed,
          });
          break;
        }
        case 'arrowdown': {
          let sumRate = state.expandedRows.includes(state.focusedRow) ? 0.6 : 1;
          if (Math.round(state.focusedRow) - state.focusedRow !== 0)
            sumRate = 0.5;
          setFocus({
            newFocusedRow: state.focusedRow + sumRate,
            shouldSelectNewFocused: !isCtrlPressed,
          });
          break;
        }
        case 'arrowleft':
        case 'arrowright': {
          setFocus({
            newFocusedColumn:
              state.focusedColumn + (actualKey === 'arrowleft' ? -1 : 1),
            shouldSelectNewFocused: !isCtrlPressed,
          });
          break;
        }
        case 'home': {
          if (isCtrlPressed)
            setFocus({ newFocusedColumn: 0, newFocusedRow: 0, ctrlKey: false });
          else setFocus({ newFocusedColumn: 0 });
          break;
        }
        case 'end': {
          if (isCtrlPressed)
            setFocus({
              newFocusedRow: Number.POSITIVE_INFINITY,
              newFocusedColumn: Number.POSITIVE_INFINITY,
              ctrlKey: false,
            });
          else
            setFocus({
              newFocusedColumn: Number.POSITIVE_INFINITY,
            });
          break;
        }
        case 'pageup': {
          setFocus({
            newFocusedRow: state.focusedRow - 5,
            shouldSelectNewFocused: !isCtrlPressed,
          });
          break;
        }
        case 'pagedown': {
          setFocus({
            newFocusedRow: state.focusedRow + 5,
            shouldSelectNewFocused: !isCtrlPressed,
          });
          break;
        }
        case ' ': {
          if (isCtrlPressed)
            setFocus({
              newFocusedRow: state.focusedRow,
            });
          break;
        }
        case 'a': {
          if (isCtrlPressed && state.allowSelection)
            state.selectedRows = state.rows.map((_, i) => i);
          break;
        }
        default:
      }
  } else
    switch (actualKey) {
      case 'escape':
      case 'enter': {
        if (state.isEditionMode) state.isEditionMode = false;
        ev.stopPropagation();
        break;
      }
      default:
        break;
    }
}
